import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/app/ProtectedRoute";
import { ErrorBoundary } from "@/components/app/ErrorBoundary";
import "@/lib/errorReporter"; // side-effect: wires window.error + unhandledrejection listeners
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import SolicitorDashboard from "@/pages/SolicitorDashboard";
import ClientDashboard from "@/pages/ClientDashboard";
import CaseDetailPage from "@/pages/CaseDetailPage";
import SignPage from "@/pages/SignPage";
import GiftorPortalPage from "@/pages/GiftorPortalPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import PortalPage from "@/pages/PortalPage";
import KanbanPage from "@/pages/KanbanPage";
import ProfileSettingsPage from "@/pages/ProfileSettingsPage";
import QuotePage from "@/pages/QuotePage";
import QuoteViewPage from "@/pages/QuoteViewPage";
import QuotesListPage from "@/pages/QuotesListPage";
import CustomQuoteBuilder from "@/pages/CustomQuoteBuilder";
import ReferrerPortalPage from "@/pages/ReferrerPortalPage";
import ReferrersPage from "@/pages/ReferrersPage";
import ReferrerDashboardPage from "@/pages/ReferrerDashboardPage";
import InvoicesPage from "@/pages/InvoicesPage";
import EmailSettingsPage from "@/pages/EmailSettingsPage";
import EmbedCodePage from "@/pages/EmbedCodePage";
import ReferrerCaseDetailPage from "@/pages/ReferrerCaseDetailPage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import AdminErrorsPage from "@/pages/AdminErrorsPage";
import AdminUsageBillingPage from "@/pages/AdminUsageBillingPage";
import Pg8AuditPage from "@/pages/Pg8AuditPage";
import Pg67AuditPage from "@/pages/Pg67AuditPage";
import SlaDashboardPage from "@/pages/SlaDashboardPage";
import MagicLinkLandingPage from "@/pages/MagicLinkLandingPage";
import ClientsPage from "@/pages/ClientsPage";
import DocumentTemplatesPage from "@/pages/DocumentTemplatesPage";
import InboxPage from "@/pages/InboxPage";
import CommsArchivePage from "@/pages/CommsArchivePage";
import IntegrationsSettingsPage from "@/pages/IntegrationsSettingsPage";
import IntegrationHealthPage from "@/pages/IntegrationHealthPage";
import MicrosoftAuthCallbackPage from "@/pages/MicrosoftAuthCallbackPage";
import ReferrerLoginPage from "@/pages/ReferrerLoginPage";
import ReferrerPasswordSetupPage from "@/pages/ReferrerPasswordSetupPage";
import AuthCallbackPage from "@/pages/AuthCallbackPage";
import AccountsPage from "@/pages/AccountsPage";
import PostCompletionDashboardPage from "@/pages/PostCompletionDashboardPage";
import AmlDashboardPage from "@/pages/AmlDashboardPage";
import AmlAuditPage from "@/pages/AmlAuditPage";
import SubmissionSettingsPage from "@/pages/SubmissionSettingsPage";
import OnboardingWizardPage from "@/pages/OnboardingWizardPage";
import FirmQuestionnairesPage from "@/pages/FirmQuestionnairesPage";
import FirmTaTemplatesPage from "@/pages/FirmTaTemplatesPage";
import CaseTa13Page from "@/pages/CaseTa13Page";
import { Toaster } from "sonner";
import "@/App.css";

function DashboardRouter() {
  const { user } = useAuth();
  if (user?.role === "solicitor") return <SolicitorDashboard />;
  if (user?.role === "referrer") {
    // Referrer logged in — bounce to their portal (existing token-keyed page)
    return <Navigate to={user.referrer_token ? `/referrer-dashboard/${user.referrer_token}` : "/referrer-login"} replace />;
  }
  return <ClientDashboard />;
}

/**
 * Handles the OAuth callback fragment (`#session_id=…`) anywhere in the app.
 * Per the Emergent Auth playbook, we detect synchronously during render so
 * the AuthCallbackPage runs BEFORE ProtectedRoute / AuthProvider would race.
 */
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallbackPage />;
  }
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/referrer-login" element={<ReferrerLoginPage />} />
      <Route path="/referrer-setup" element={<ReferrerPasswordSetupPage />} />
      <Route path="/referrer-setup/:token" element={<ReferrerPasswordSetupPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/portal/:token" element={<PortalPage />} />
      <Route path="/sign/:inviteId" element={<SignPage />} />
      <Route path="/giftor/:inviteToken" element={<GiftorPortalPage />} />
      <Route path="/quote" element={<QuotePage />} />
      <Route path="/quote/referrer" element={<QuotePage isReferrer />} />
      <Route path="/referrers-web-tool" element={<QuotePage bespokeReferrer />} />
      <Route path="/quote/view/:quoteId" element={<QuoteViewPage />} />
      <Route path="/referrer/:token" element={<ReferrerPortalPage />} />
      <Route path="/referrer-dashboard/:token" element={<ReferrerDashboardPage />} />
      <Route path="/referrer-dashboard/:token/case/:caseId" element={<ReferrerCaseDetailPage />} />
      <Route path="/" element={<ProtectedRoute><DashboardRouter /></ProtectedRoute>} />
      <Route path="/cases" element={<ProtectedRoute><DashboardRouter /></ProtectedRoute>} />
      <Route path="/cases/:caseId" element={<ProtectedRoute><CaseDetailPage /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/accounts" element={<ProtectedRoute><AccountsPage /></ProtectedRoute>} />
      <Route path="/kanban" element={<ProtectedRoute><KanbanPage /></ProtectedRoute>} />
      <Route path="/quotes" element={<ProtectedRoute><QuotesListPage /></ProtectedRoute>} />
      <Route path="/quotes/custom" element={<ProtectedRoute><CustomQuoteBuilder /></ProtectedRoute>} />
      <Route path="/referrers" element={<ProtectedRoute><ReferrersPage /></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute><InvoicesPage /></ProtectedRoute>} />
      <Route path="/email-settings" element={<ProtectedRoute><EmailSettingsPage /></ProtectedRoute>} />
      <Route path="/embed-code" element={<ProtectedRoute><EmbedCodePage /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute><AdminUsersPage /></ProtectedRoute>} />
      <Route path="/admin/errors" element={<ProtectedRoute><AdminErrorsPage /></ProtectedRoute>} />
      <Route path="/admin/usage-billing" element={<ProtectedRoute><AdminUsageBillingPage /></ProtectedRoute>} />
      <Route path="/admin/pg8-audit" element={<ProtectedRoute><Pg8AuditPage /></ProtectedRoute>} />
      <Route path="/admin/pg67-audit" element={<ProtectedRoute><Pg67AuditPage /></ProtectedRoute>} />
      <Route path="/admin/sla" element={<ProtectedRoute><SlaDashboardPage /></ProtectedRoute>} />
      <Route path="/portal-login/:token" element={<MagicLinkLandingPage />} />
      <Route path="/clients" element={<ProtectedRoute><ClientsPage /></ProtectedRoute>} />
      <Route path="/templates" element={<ProtectedRoute><DocumentTemplatesPage /></ProtectedRoute>} />
      <Route path="/inbox" element={<ProtectedRoute><InboxPage /></ProtectedRoute>} />
      <Route path="/inbox/archive" element={<ProtectedRoute><CommsArchivePage /></ProtectedRoute>} />
      <Route path="/settings/integrations" element={<ProtectedRoute><IntegrationsSettingsPage /></ProtectedRoute>} />
      <Route path="/settings/integrations/health" element={<ProtectedRoute><IntegrationHealthPage /></ProtectedRoute>} />
      <Route path="/auth/microsoft/callback" element={<ProtectedRoute><MicrosoftAuthCallbackPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><ProfileSettingsPage /></ProtectedRoute>} />
      <Route path="/post-completion" element={<ProtectedRoute><PostCompletionDashboardPage /></ProtectedRoute>} />
      <Route path="/aml" element={<ProtectedRoute><AmlDashboardPage /></ProtectedRoute>} />
      <Route path="/aml/audit" element={<ProtectedRoute><AmlAuditPage /></ProtectedRoute>} />
      <Route path="/settings/submissions" element={<ProtectedRoute><SubmissionSettingsPage /></ProtectedRoute>} />
      <Route path="/client/onboarding/:caseId" element={<ProtectedRoute><OnboardingWizardPage /></ProtectedRoute>} />
      <Route path="/onboarding/cases/:caseId" element={<ProtectedRoute><OnboardingWizardPage /></ProtectedRoute>} />
      <Route path="/settings/questionnaires" element={<ProtectedRoute><FirmQuestionnairesPage /></ProtectedRoute>} />
      <Route path="/settings/ta-templates" element={<ProtectedRoute><FirmTaTemplatesPage /></ProtectedRoute>} />
      <Route path="/cases/:caseId/ta13" element={<ProtectedRoute><CaseTa13Page /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
